import JobApi from "../generated/api/JobApi";

class JobService {
  constructor() {
    this.JobApi = new JobApi();
  }

  async getJobById(jobId) {
    return await window.$A.HandleApiResponse(this.JobApi.getJobById(jobId));
  }

  async updateJobById(updateJobRequest) {
    return await window.$A.HandleApiResponse(
      this.JobApi.updateJob(updateJobRequest.jobId, {
        jobRequest: updateJobRequest.jobData,
      }),
      "Job Updated Successfully"
    );
  }

  async createJob(createJobRequest) {
    return await window.$A.HandleApiResponse(
      this.JobApi.createJob({ jobRequest: createJobRequest }),
      "Job Created Successfully"
    );
  }

  async deleteJob(jobId) {
    return await window.$A.HandleApiResponse(
      this.JobApi.deleteJob(jobId),
      "Job Deleted Successfully"
    );
  }

  async duplicateJob(jobId) {
    return await window.$A.HandleApiResponse(
      this.JobApi.duplicateJob(jobId),
      "Job Duplicated Successfully"
    );
  }

  // Unified job list fetch. scope: "active" (default) or "archived".
  async listJobs({ query = null, archived = false } = {}) {
    const EnvService = (await import("../env.settings")).default;
    const token = window.$A.Auth.apiToken.token;
    const base = EnvService.instance.apiHost.replace(/\/+$/, "");
    const params = new URLSearchParams();
    if (query && String(query).trim()) params.set("q", String(query).trim());
    if (archived) params.set("archived", "true");
    params.set("access_token", token);
    const url = `${base}/jobs?${params.toString()}`;
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error(`Fetch jobs failed: HTTP ${res.status}`);
    return await res.json().catch(() => []);
  }

  async archiveJob(jobId) {
    const EnvService = (await import("../env.settings")).default;
    const token = window.$A.Auth.apiToken.token;
    const base = EnvService.instance.apiHost.replace(/\/+$/, "");
    const url = `${base}/jobs/${jobId}/archive?access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { method: "POST" });
    if (!res.ok) throw new Error(`Archive job failed: HTTP ${res.status}`);
    window.$A.AlertUser("Job Archived Successfully", "success");
    return await res.json().catch(() => ({}));
  }

  async unarchiveJob(jobId) {
    const EnvService = (await import("../env.settings")).default;
    const token = window.$A.Auth.apiToken.token;
    const base = EnvService.instance.apiHost.replace(/\/+$/, "");
    const url = `${base}/jobs/${jobId}/unarchive?access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { method: "POST" });
    if (!res.ok) throw new Error(`Unarchive job failed: HTTP ${res.status}`);
    window.$A.AlertUser("Job Restored Successfully", "success");
    return await res.json().catch(() => ({}));
  }

  async updateAndDeleteProofJobLink(proofJobLinkRequest) {
    return await window.$A.HandleApiResponse(
      this.JobApi.updateAndDeleteProofJobLink({modifyJobProofLinkRequest: proofJobLinkRequest}),
      "Proof Job Link Updated Successfully"
    );
  }

  async getJobFiles(jobId) {
    return await window.$A.HandleApiResponse(this.JobApi.getJobFiles(jobId));
  }

  async updateContactLink({ jobId, update = [], delete: removeIds = [] }) {
    const EnvService = (await import("../env.settings")).default;
    const token = window.$A.Auth.apiToken.token;
    const base = EnvService.instance.apiHost.replace(/\/+$/, "");
    const url = `${base}/jobs/contacts?access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jobId, update, delete: removeIds }),
    });
    if (!res.ok) throw new Error(`Update contacts failed: HTTP ${res.status}`);
    window.$A.AlertUser("Contacts updated", "success");
    return await res.json().catch(() => ({}));
  }

  async deleteJobFile(jobId, fileId) {
    const EnvService = (await import("../env.settings")).default;
    const token = window.$A.Auth.apiToken.token;
    const base = EnvService.instance.apiHost.replace(/\/+$/, "");
    const url = `${base}/jobs/${jobId}/files/${fileId}?access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { method: "DELETE" });
    if (!res.ok) {
      throw new Error(`Delete failed: HTTP ${res.status}`);
    }
    window.$A.AlertUser("File Deleted Successfully", "success");
  }

  async apiBase() {
    const EnvService = (await import("../env.settings")).default;
    return EnvService.instance.apiHost.replace(/\/+$/, "");
  }

  // Records that the current user opened the job; clears its "new activity" counts on the board.
  async markViewed(jobId) {
    const base = await this.apiBase();
    const token = window.$A.Auth.apiToken.token;
    const url = `${base}/jobs/${jobId}/viewed?access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { method: "POST" });
    if (!res.ok) throw new Error(`Mark viewed failed: HTTP ${res.status}`);
  }

  reportParams({ start = null, end = null, groupBy = null, staleDays = null } = {}) {
    const params = new URLSearchParams();
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (groupBy) params.set("groupBy", groupBy);
    if (staleDays) params.set("staleDays", String(staleDays));
    params.set("access_token", window.$A.Auth.apiToken.token);
    return params;
  }

  async getReport(options = {}) {
    const base = await this.apiBase();
    const res = await fetch(`${base}/jobs/report?${this.reportParams(options).toString()}`, { method: "GET" });
    if (!res.ok) throw new Error(`Fetch job report failed: HTTP ${res.status}`);
    return await res.json();
  }

  async reportExportUrl(options = {}) {
    const base = await this.apiBase();
    return `${base}/jobs/report/export?${this.reportParams(options).toString()}`;
  }

  async moveJob(jobId, newStatusId, InsertAfterRank = null, InsertBeforeRank = null) {
    return await window.$A.HandleApiResponse(
      this.JobApi.moveJob({
        moveJobRequest: {
          jobId: jobId,
          newStatusId: newStatusId,
          InsertAfterRank: InsertAfterRank,
          InsertBeforeRank: InsertBeforeRank
        }
      })
    );
  }
}

export default JobService;
